import { useState, useCallback, Dispatch, SetStateAction } from "react";
import { Responses } from "@/components/Quiz/useQuiz";
import { AnswerListPossibility } from "@WahooFitness/cloud-client-types";
import { DragEndEvent } from "@dnd-kit/core";

interface Props {
  initialVal?: Array<string>;
  options: Array<AnswerListPossibility>;
  setResponses: Dispatch<SetStateAction<Responses | undefined>>;
  questionId: number;
  setActiveId: Dispatch<SetStateAction<string | null>>;
}

function useRanking({ initialVal, options, setResponses, questionId, setActiveId }: Props) {
  function orderOptions(valuesList: Array<string>) {
    return valuesList.map(
      (value) => options.find((option) => option.key === value) as AnswerListPossibility
    );
  }
  // state property to hold the values to show for the rankItem
  const [orderedOptions, setOrderedOptions] = useState(
    !initialVal || initialVal[0] === "skip" ? options : orderOptions(initialVal)
  );

  const [skipChecked, setSkipChecked] = useState(initialVal?.[0] === "skip");

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = orderedOptions.findIndex((i) => i.key === active.id);
      const newIndex = orderedOptions.findIndex((i) => i.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = [...orderedOptions];
      const [movedItem] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, movedItem);
      setOrderedOptions(newOrder);
      setResponses((prev) => {
        if (prev) {
          prev[questionId] = newOrder.map((i) => i.key);
        }
        return prev;
      });
      setActiveId(null);
    },
    [orderedOptions, questionId, setActiveId, setResponses]
  );

  const handleSkipToggle = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      if (ev.target.checked) {
        setSkipChecked(true);
        setResponses((prev: any) => {
          prev[questionId] = ["skip"];
          return prev;
        });
      } else {
        setSkipChecked(false);
        setResponses((prev: any) => {
          prev[questionId] = orderedOptions.map((i) => i.key);
          return prev;
        });
      }
    },
    [questionId, setResponses, orderedOptions]
  );

  return {
    onDragEnd,
    orderedOptions,
    skipChecked,
    handleSkipToggle,
  };
}

export default useRanking;
